import React, { useState, useEffect } from "react";
import Agenda from "./Agenda";
import agendaService from "../services/agendaService";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import usuarioService from "../services/usuarioService";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { hideProcessing, showProcessing } from "../utils/utils";

/**
 * Exemplo de página que utiliza o componente Agenda
 * Esta página carrega os agendamentos do backend e os exibe no calendário
 */

let _agendamentoInitialized = false;

export default function AgendaPage() {
    const [agendamentos, setAgendamentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const toastRef = useRef(null);
    const [mostrarModalBloqueio, setMostrarModalBloqueio] = useState(false);
    const [dataInicio, setDataInicio] = useState(null);
    const [horaInicio, setHoraInicio] = useState("09:00");
    const [dataFim, setDataFim] = useState(null);
    const [horaFim, setHoraFim] = useState("10:00");
    const [descricaoBloqueio, setDescricaoBloqueio] = useState("");
    const [carregandoBloqueio, setCarregandoBloqueio] = useState(false);

    /**
     * Carrega os agendamentos ao montar o componente
     */
    useEffect(() => {
        if (!_agendamentoInitialized) {
            _agendamentoInitialized = true;
            return;
        }

        carregarAgendamentos();
    }, []);

    /**
     * Função para carregar agendamentos do backend (com dados simulados)
     */
    const carregarAgendamentos = async () => {
        try {
            setLoading(true);
            
            const response = await usuarioService.getAgenda();

            setAgendamentos(response.data);
            
            
        } catch (error) {
            console.error("Erro ao carregar agendamentos:", error);
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Não foi possível carregar os agendamentos",
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Função para criar um bloqueio de agenda
     */
    const criarBloqueioAgenda = async () => {
        try {
            // Validações
            if (!dataInicio) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Informe a data de início",
                    life: 3000
                });
                return;
            }

            if (!horaInicio || !horaInicio.match(/^\d{2}:\d{2}$/)) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Informe a hora de início no formato HH:MM",
                    life: 3000
                });
                return;
            }

            if (!dataFim) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Informe a data de fim",
                    life: 3000
                });
                return;
            }

            if (!horaFim || !horaFim.match(/^\d{2}:\d{2}$/)) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Informe a hora de fim no formato HH:MM",
                    life: 3000
                });
                return;
            }

            // Combinar data e hora
            const [horaInicioH, horaInicioM] = horaInicio.split(':').map(Number);
            const [horaFimH, horaFimM] = horaFim.split(':').map(Number);

            const dataCompromisso = new Date(dataInicio);
            dataCompromisso.setHours(horaInicioH, horaInicioM, 0, 0);

            const finalCompromisso = new Date(dataFim);
            finalCompromisso.setHours(horaFimH, horaFimM, 0, 0);

            if (finalCompromisso <= dataCompromisso) {
                toastRef.current?.show({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "A data/hora de fim deve ser após o início",
                    life: 3000
                });
                return;
            }

            setCarregandoBloqueio(true);
            showProcessing();

            // Criar objeto de bloqueio
            const bloqueio = {
                dataCompromisso: dataCompromisso,
                finalCompromisso: finalCompromisso,
                status: "BLOQUEADO",
                tipo: "BLOQUEIO",
                observacoes: descricaoBloqueio || "Bloqueio de agenda"
            };

            // Fazer chamada ao serviço
            const response = await agendaService.bloquear(bloqueio);

            if (response && response.data) {
                // Recarregar agendamentos
                await carregarAgendamentos();

                toastRef.current?.show({
                    severity: "success",
                    summary: "Sucesso",
                    detail: "Bloqueio criado com sucesso!",
                    life: 3000
                });

                // Limpar modal
                setMostrarModalBloqueio(false);
                setDataInicio(null);
                setHoraInicio("09:00");
                setDataFim(null);
                setHoraFim("10:00");
                setDescricaoBloqueio("");
            }
        } catch (error) {
            if (error.response && error.response.data) {
                toastRef.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: error.response.data,
                    life: 3000
                });
            } else {
                console.error("Erro ao criar bloqueio de agenda:", error);
                toastRef.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: "Erro ao criar bloqueio de agenda",
                    life: 3000
                });
            }
        } finally {
            setCarregandoBloqueio(false);
            hideProcessing();
        }
    };

    if (loading) {
        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh"
            }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <>
            <Toast ref={toastRef} />
            
            {/* Botão para bloquear agenda */}
            <div style={{
                padding: "15px",
                display: "flex",
                justifyContent: "flex-end"
            }}>
                <Button
                    label="Bloquear Agenda"
                    icon="pi pi-lock"
                    onClick={() => setMostrarModalBloqueio(true)}
                    severity="warning"
                    style={{ padding: "10px 20px" }}
                />
            </div>

            {/* Modal para criar bloqueio */}
            <Dialog
                visible={mostrarModalBloqueio}
                onHide={() => setMostrarModalBloqueio(false)}
                header="Bloquear Agenda"
                modal
                style={{ width: "90vw", maxWidth: "600px" }}
            >
                <div style={{ padding: "20px 0" }}>
                    <div style={{ marginBottom: "20px" }}>
                        <h4 style={{ margin: "0 0 15px 0", color: "#333", fontSize: "13px", fontWeight: "600" }}>
                            📅 Data e Hora de Início
                        </h4>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "15px"
                        }}>
                            <div>
                                <label style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "600",
                                    color: "#333",
                                    fontSize: "12px"
                                }}>
                                    Data
                                </label>
                                <Calendar
                                    value={dataInicio}
                                    onChange={(e) => setDataInicio(e.value)}
                                    placeholder="Selecione a data"
                                    style={{ width: "100%" }}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "600",
                                    color: "#333",
                                    fontSize: "12px"
                                }}>
                                    Hora (HH:MM)
                                </label>
                                <InputText
                                    value={horaInicio}
                                    onChange={(e) => setHoraInicio(e.target.value)}
                                    placeholder="09:00"
                                    maxLength="5"
                                    style={{ width: "100%", fontSize: "13px" }}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <h4 style={{ margin: "0 0 15px 0", color: "#333", fontSize: "13px", fontWeight: "600" }}>
                            📅 Data e Hora de Fim
                        </h4>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "15px"
                        }}>
                            <div>
                                <label style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "600",
                                    color: "#333",
                                    fontSize: "12px"
                                }}>
                                    Data
                                </label>
                                <Calendar
                                    value={dataFim}
                                    onChange={(e) => setDataFim(e.value)}
                                    placeholder="Selecione a data"
                                    minDate={dataInicio}
                                    style={{ width: "100%" }}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "600",
                                    color: "#333",
                                    fontSize: "12px"
                                }}>
                                    Hora (HH:MM)
                                </label>
                                <InputText
                                    value={horaFim}
                                    onChange={(e) => setHoraFim(e.target.value)}
                                    placeholder="10:00"
                                    maxLength="5"
                                    style={{ width: "100%", fontSize: "13px" }}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <label style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "600",
                            color: "#333",
                            fontSize: "13px"
                        }}>
                            📝 Descrição (Opcional)
                        </label>
                        <InputText
                            value={descricaoBloqueio}
                            onChange={(e) => setDescricaoBloqueio(e.target.value)}
                            placeholder="Ex: Reunião, Manutenção, etc."
                            style={{ width: "100%", fontSize: "13px" }}
                        />
                    </div>

                    <div style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "flex-end"
                    }}>
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            onClick={() => {
                                setMostrarModalBloqueio(false);
                                setDataInicio(null);
                                setHoraInicio("09:00");
                                setDataFim(null);
                                setHoraFim("10:00");
                                setDescricaoBloqueio("");
                            }}
                            severity="secondary"
                            disabled={carregandoBloqueio}
                        />
                        <Button
                            label="Bloquear"
                            icon="pi pi-lock"
                            onClick={criarBloqueioAgenda}
                            loading={carregandoBloqueio}
                            severity="warning"
                        />
                    </div>
                </div>
            </Dialog>

            <Agenda agendamentos={agendamentos} removerBloqueio={true}/>
        </>
    );
}
